import httpClient from './httpClient';

/**
 * Document generation service for SOW documents
 * Handles Google Docs generation and PDF conversion
 */
class DocumentService {
  static resolveTargetIds(sowId, documentContext) {
    return {
      action: documentContext?.action,
      sowId: documentContext?.draft_sow_id || sowId,
      addendumId: documentContext?.draft_addendum_id || null,
    };
  }

  static buildGenerationEndpoint(target, suffix) {
    if (target.action === 'generate_addendum') {
      if (!target.addendumId) {
        throw new Error('Draft addendum id is required');
      }
      return `/api/v1/project-management/addendums/${target.addendumId}/${suffix}/`;
    }

    if (!target.sowId) {
      throw new Error('SOW id is required');
    }
    return `/api/v1/project-management/sows/${target.sowId}/${suffix}/`;
  }

  /**
   * Generate Google Docs for SOW or Addendum
   * @param {string} sowId - SOW ID
   * @param {string[]} audience - Audience array: ['client'], ['freelancer'], or ['client', 'freelancer'] (default)
   * @param {Object} documentContext - Document context with action and draft IDs
   * @returns {Promise} API response
   */
  static async generateDocument(
    sowId,
    audience = ['client', 'freelancer'],
    documentContext = null
  ) {
    try {
      const target = DocumentService.resolveTargetIds(sowId, documentContext);
      const endpoint = DocumentService.buildGenerationEndpoint(
        target,
        'generate-document'
      );

      const requestBody = { audience };
      if (target.action) {
        requestBody.action = target.action;
      }

      const response = await httpClient.post(endpoint, requestBody);
      return response;
    } catch (error) {
      throw new Error(error?.message || 'Failed to generate document');
    }
  }

  /**
   * Generate template-based documents for SOW or Addendum (Docuseal)
   * @param {string} sowId - SOW ID
   * @param {string[]} audience - Audience array: ['client'], ['freelancer'], or ['client', 'freelancer'] (default)
   * @param {Object} documentContext - Document context with action and draft IDs
   * @returns {Promise} API response
   */
  static async generateTemplate(
    sowId,
    audience = ['client', 'freelancer'],
    documentContext = null
  ) {
    try {
      const target = DocumentService.resolveTargetIds(sowId, documentContext);
      const endpoint = DocumentService.buildGenerationEndpoint(
        target,
        'generate-template'
      );

      const requestBody = { audience };
      if (target.action) {
        requestBody.action = target.action;
      }

      const response = await httpClient.post(endpoint, requestBody);
      return response;
    } catch (error) {
      throw new Error(error?.message || 'Failed to generate template');
    }
  }

  /**
   * Generate PDFs from existing Google Docs
   * @param {string} sowId - SOW ID
   * @param {string} docType - Document type: 'client', 'freelancer', or 'both' (default)
   * @returns {Promise} API response
   */
  static async generatePDF(sowId, docType = 'both', documentContext = null) {
    try {
      const target = DocumentService.resolveTargetIds(sowId, documentContext);
      const endpoint = DocumentService.buildGenerationEndpoint(
        target,
        'generate-pdf'
      );

      const response = await httpClient.post(endpoint, { audience: docType });
      return response;
    } catch (error) {
      throw new Error(error?.message || 'Failed to generate PDF');
    }
  }

  /**
   * Get SOW documents list
   * @param {string} sowId - SOW ID
   * @returns {Promise} API response
   */
  static async getDocuments(sowId) {
    try {
      const response = await httpClient.get(
        `/api/v1/project-management/sows/${sowId}/documents/`
      );
      return response;
    } catch (error) {
      throw new Error(error?.message || 'Failed to fetch documents');
    }
  }

  /**
   * Send document via email
   * @param {string} sowId - SOW ID
   * @param {string} documentId - Document ID
   * @returns {Promise} API response
   */
  static async sendDocumentEmail(sowId, documentId) {
    try {
      const response = await httpClient.post(
        `/api/v1/project-management/sows/${sowId}/documents/${documentId}/send-email/`
      );
      return response;
    } catch (error) {
      throw new Error(error?.message || 'Failed to send email');
    }
  }

  /**
   * Delete document
   * @param {string} sowId - SOW ID
   * @param {string} documentId - Document ID
   * @returns {Promise} API response
   */
  static async deleteDocument(sowId, documentId) {
    try {
      const response = await httpClient.delete(
        `/api/v1/project-management/sows/${sowId}/documents/${documentId}/`
      );
      return response;
    } catch (error) {
      throw new Error(error?.message || 'Failed to delete document');
    }
  }

  /**
   * Check if document generation is possible
   * @param {Object} sow - SOW object
   * @returns {Object} Generation status and requirements
   */
  static getGenerationStatus(sow) {
    const isTimesheetView = sow?.budget_model === 'hourly';
    const canGenerate = isTimesheetView || sow?.milestone_count > 0;
    const hasDocuments = sow?.client_document || sow?.freelancer_document;
    const canGeneratePDF = hasDocuments;

    const requirements = [];
    if (!isTimesheetView && !canGenerate) {
      requirements.push('At least 1 milestone is required.');
    }
    if (!canGeneratePDF && hasDocuments === false) {
      requirements.push('Generate SOW');
    }

    return {
      canGenerateDocument: canGenerate,
      canGeneratePDF,
      hasExistingDocuments: hasDocuments,
      requirements,
      documentsCount: [sow?.client_document, sow?.freelancer_document].filter(
        Boolean
      ).length,
    };
  }

  /**
   * Format generation response for state updates
   * @param {Object} response - API response
   * @param {string} type - 'document' or 'pdf'
   * @returns {Object} Formatted response with SOW-compatible document structure
   */
  static formatGenerationResponse(response, type = 'document') {
    const { status, message, data } = response;

    if (!status) {
      return {
        success: false,
        message: message || `Failed to generate ${type}`,
        updatedDocuments: {},
      };
    }

    const updatedDocuments = {};

    // Format client document if present
    if (data.client) {
      updatedDocuments.client_document = {
        id:
          data.client.doc_id ||
          data.client.id ||
          `client-${data.client.sow_id || 'doc'}`,
        audience: 'client',
        version: data.client.version || 1,
        pdf_link: data.client.pdf_link || null,
        pdf_name: data.client.pdf_name || null,
        email_sent_at: data.client.email_sent_at || null,
      };
    }

    // Format freelancer document if present
    if (data.freelancer) {
      updatedDocuments.freelancer_document = {
        id:
          data.freelancer.doc_id ||
          data.freelancer.id ||
          `freelancer-${data.freelancer.sow_id || 'doc'}`,
        audience: 'freelancer',
        version: data.freelancer.version || 1,
        pdf_link: data.freelancer.pdf_link || null,
        pdf_name: data.freelancer.pdf_name || null,
        email_sent_at: data.freelancer.email_sent_at || null,
      };
    }

    // Handle single document response
    if (!data.client && !data.freelancer && data.audience) {
      const documentKey =
        data.audience === 'client' ? 'client_document' : 'freelancer_document';
      updatedDocuments[documentKey] = {
        id:
          data.doc_id || data.id || `${data.audience}-${data.sow_id || 'doc'}`,
        audience: data.audience,
        version: data.version || 1,
        pdf_link: data.pdf_link || null,
        pdf_name: data.pdf_name || null,
        email_sent_at: data.email_sent_at || null,
      };
    }

    return {
      success: true,
      message:
        message ||
        `${type === 'document' ? 'Document' : 'PDF'} generated successfully`,
      updatedDocuments,
      isPartialSuccess: message?.includes('failed') || false,
    };
  }
}

export default DocumentService;
